/**
 * The Sweeper's call outcomes and what they drive (B7, diagram 08):
 *  - a turn-down takes the client off THAT gap's list (8xat D08-16);
 *  - a "No answer" / "Call me back" makes the Sweeper who called the owner of the
 *    client's callback for 7 days (75nh D08-26; Anas 1 Oct Q3), shown in their
 *    Callbacks band and rung through to them alone (simulated calls only);
 *  - the "Unfinished business" list: no-rebook mid-piece clients, whatever gaps
 *    their artist has this week (75mt; Anas Q4).
 * Pure reads over the store's records; the write is `recordSweeperOutcome`.
 */
import { isLiveUpcomingSlot, studioDayKey } from '@atink/domain';
import type {
  Artist,
  Booking,
  CustomerRecord,
  Deposit,
  NoRebookFlag,
  Piece,
  SweeperOutcome,
  SweeperOutcomeKind,
} from '@/domain/types';

/** How long a callback stays routed to the Sweeper who called (Anas Q3). */
export const SWEEPER_CALLBACK_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** The gap's key: `<artistId>-<studio day>`, the same id the Sweeper's gap rows carry. */
export function gapKeyFor(artistId: string, dayIso: string): string {
  return `${artistId}-${studioDayKey(dayIso)}`;
}

/** One outcome per gap and client. */
export function sweeperOutcomeId(gapKey: string, customerId: string): string {
  return `swo-${gapKey}-${customerId}`;
}

/** Clients who turned THIS gap down. */
export function turnedDownFor(outcomes: Record<string, SweeperOutcome> | undefined, gapKey: string): Set<string> {
  const out = new Set<string>();
  for (const o of Object.values(outcomes ?? {})) {
    if (o.gapKey === gapKey && o.outcome === 'turned-down') out.add(o.customerId);
  }
  return out;
}

/**
 * The client's most recent outcome on any gap, as of `nowIso`. An outcome dated
 * after now (a clock moved back, or a forged `at`) is ignored, so it can never
 * hold a client's callbacks for good.
 */
function latestOutcome(
  outcomes: Record<string, SweeperOutcome> | undefined,
  customerId: string,
  nowIso: string,
): SweeperOutcome | undefined {
  const now = Date.parse(nowIso);
  let latest: SweeperOutcome | undefined;
  for (const o of Object.values(outcomes ?? {})) {
    if (o.customerId !== customerId) continue;
    const t = Date.parse(o.at);
    if (!Number.isFinite(t) || t > now) continue;
    if (!latest || t > Date.parse(latest.at)) latest = o;
  }
  return latest;
}

/**
 * Who a call from this client rings (D08-26): the Sweeper whose latest call to
 * them — a "No answer" or "Call me back" — is inside the last 7 days, unless
 * someone else has reached them since (a later outbound contact by another
 * person). Undefined: no owner, ring anyone.
 */
export function routeInboundCall(args: {
  customer: CustomerRecord | undefined;
  outcomes: Record<string, SweeperOutcome> | undefined;
  nowIso: string;
}): { pid: string; outcome: SweeperOutcome } | undefined {
  const { customer, outcomes, nowIso } = args;
  if (!customer) return undefined;
  const latest = latestOutcome(outcomes, customer.id, nowIso);
  // Only a call that didn't land owes a callback: "No answer" / "Call me back".
  // A client who turned the slot down was reached — their next call rings anyone.
  if (!latest || latest.outcome === 'turned-down') return undefined;
  const at = Date.parse(latest.at);
  const now = Date.parse(nowIso);
  if (!(now - at <= SWEEPER_CALLBACK_DAYS * DAY_MS)) return undefined;
  const reachedByOther = customer.conversation.some(
    (e) => e.direction === 'out' && !!e.agentId && e.agentId !== latest.by && Date.parse(e.timestamp) > at,
  );
  if (reachedByOther) return undefined;
  return { pid: latest.by, outcome: latest };
}

export interface SweeperCallbackRow {
  customer: CustomerRecord;
  outcome: SweeperOutcome;
  /** When the callback stops routing to this Sweeper. */
  routedUntilIso: string;
}

/**
 * The Callbacks band (D08-26): clients whose callback routes to `pid` — their
 * latest outcome is that Sweeper's "No answer" or "Call me back", inside 7 days.
 * Newest first. It reads the persisted outcomes, so it survives a reload and
 * shows on another device.
 */
export function buildSweeperCallbacks(args: {
  outcomes: Record<string, SweeperOutcome> | undefined;
  customers: Record<string, CustomerRecord>;
  pid: string | undefined;
  nowIso: string;
}): SweeperCallbackRow[] {
  const { outcomes, customers, pid, nowIso } = args;
  if (!pid) return [];
  const rows: SweeperCallbackRow[] = [];
  const seen = new Set<string>();
  for (const o of Object.values(outcomes ?? {})) {
    if (seen.has(o.customerId)) continue;
    seen.add(o.customerId);
    const customer = customers[o.customerId];
    if (!customer || customer.doNotContact) continue;
    const route = routeInboundCall({ customer, outcomes, nowIso });
    if (!route || route.pid !== pid) continue;
    rows.push({
      customer,
      outcome: route.outcome,
      routedUntilIso: new Date(Date.parse(route.outcome.at) + SWEEPER_CALLBACK_DAYS * DAY_MS).toISOString(),
    });
  }
  return rows.sort((a, b) => Date.parse(b.outcome.at) - Date.parse(a.outcome.at));
}

/** The candidate-row label for an outcome that leaves the client listed. */
export function outcomeLabel(kind: SweeperOutcomeKind): string {
  if (kind === 'no-answer') return 'Called · no answer';
  if (kind === 'call-back') return 'Called · call back';
  return 'Turned this gap down';
}

export interface UnfinishedBusinessRow {
  flag: NoRebookFlag;
  customer: CustomerRecord;
  piece: Piece | undefined;
  artist: Artist | undefined;
  /** Days since the client's most recent completed tattoo (the recently-tattooed flag). */
  tattooedDays: number | undefined;
}

const PIECE_DONE: ReadonlySet<Piece['status']> = new Set(['completed', 'single-piece-done', 'abandoned', 'abandoned-large']);

/**
 * The Sweeper's standing "Unfinished business" list (75mt; Anas Q4): every
 * unresolved no-rebook mid-piece flag whose piece is still open and whose client
 * hasn't booked again, with "tattooed N days ago" — whatever gaps their artist
 * has. Filters on the flag's `resolvedAt` and the piece's status only, so a rule
 * that clears flags (B8: completing the piece) flows straight through.
 * Oldest flag first (it has waited longest).
 */
export function buildSweeperUnfinishedBusiness(args: {
  noRebookFlags: Record<string, NoRebookFlag>;
  pieces: Record<string, Piece>;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  customers: Record<string, CustomerRecord>;
  artists: Record<string, Artist>;
  nowIso: string;
}): UnfinishedBusinessRow[] {
  const { noRebookFlags, pieces, bookings, deposits, customers, artists, nowIso } = args;
  const now = Date.parse(nowIso);
  const rows: UnfinishedBusinessRow[] = [];
  const seen = new Set<string>();
  const flags = Object.values(noRebookFlags).sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt));
  for (const flag of flags) {
    if (flag.resolvedAt) continue;
    const piece = pieces[flag.pieceId];
    if (piece && PIECE_DONE.has(piece.status)) continue;
    const customer = customers[flag.customerId];
    if (!customer || customer.doNotContact) continue;
    if (seen.has(customer.id)) continue;
    const custBookings = customer.bookings.map((id) => bookings[id]).filter((b): b is Booking => !!b);
    const custDeposits = customer.deposits.map((id) => deposits[id]).filter((d): d is Deposit => !!d);
    if (custBookings.some((b) => isLiveUpcomingSlot(b, custDeposits, nowIso))) continue; // booked again
    const lastDone = custBookings
      .filter((b) => b.status === 'completed')
      .reduce((max, b) => Math.max(max, Date.parse(b.start)), -Infinity);
    seen.add(customer.id);
    rows.push({
      flag,
      customer,
      piece,
      artist: piece ? artists[piece.assignedArtistId] : undefined,
      tattooedDays: lastDone > -Infinity ? Math.max(0, Math.round((now - lastDone) / DAY_MS)) : undefined,
    });
  }
  return rows;
}
